import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Form, Formik, FormikProps } from "formik";
import { Trans, useTranslation } from "i18n";
import {
  ActionsFooter,
  Button,
  CommonErrorPopup,
  InputLabel,
  LocationList,
  QuestionTooltip,
  SelectFormik,
} from "component-library";
import { useScrollToTop } from "shared-hooks";
import { Location } from "shared-services";
import { ILocationContract } from "component-library/src/components/Organisms/LocationList/LocationList";

import locationService from "../../../../services/location";
import { webMediaService } from "../../../../services";
import PageTrackingLayoutWrapper from "../components/PageTrackingLayoutWrapper";
import { PageContent } from "../../../../components/layout/PageContent";
import { useAgencyOptions } from "../../../../hooks/useAgencyOptions";
import GenderSelection from "../../Performers/CommonPages/AboutYouPage/AboutYouForm/GenderSelection";
import {
  getDetailsPageValidation,
  getInitialValues,
} from "./detailsPageValidation";
import useSubmitGradsApplicationForm from "../hooks/useSubmitGradsApplicationForm";
import { useGetQueryGradsApplicationData } from "../hooks/useGetGradsApplicationData";
import { useApplication } from "../../../../context/ApplicationContext";
import AboutYouFormPart1 from "../../Performers/CommonPages/AboutYouPage/AboutYouForm/AboutYouFormPart1";
import { mapHeadshotDataToBEModel } from "../../Performers/CommonPages/AboutYouPage/AboutYouForm/AboutYouForm.helpers";
import { IAboutYouFormPhotoModel } from "../../../../formModels/AboutYouFormModel";
import {
  DetailsPageFormFields,
  IGraduatesDetailsFormFields,
} from "./constants";
import { GraduatesEndpoints } from "../constants";
import { JOINING_APP_ROUTES } from "../../../../config/common";
import { useGetLocations } from "../../../../hooks/useGetLocations";

const DetailsPageGrad = () => {
  useScrollToTop();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { onlineApplicationId, securityKey, getAuthParams } = useApplication();
  const headshotFormRef = useRef<FormikProps<IAboutYouFormPhotoModel>>(null);
  const [showErrorPopUp, setShowErrorPopUp] = useState(false);
  const [submittingHeadshot, setSubmittingHeadshot] = useState(false);
  const [submittingForms, setSubmittingForms] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [headshotUploadError, setHeadshotUploadError] = useState(false);
  const [headshotFormValid, setHeadshotFormValid] = useState(false);

  const { agencyOptions, fetchingAgencyOptions } = useAgencyOptions();
  const { data: graduatesApplication, isLoading } =
    useGetQueryGradsApplicationData();

  const locationIds = graduatesApplication?.locations || [];
  const { data: locationsData = [], isLoading: locationsIsLoading } =
    useGetLocations(locationIds, !!locationIds.length);

  const { submitForm, error } = useSubmitGradsApplicationForm(
    onlineApplicationId,
    securityKey,
    () => setShowErrorPopUp(true),
    GraduatesEndpoints.Details
  );

  const formatData = (data: IGraduatesDetailsFormFields) => {
    return {
      publishedGender: data[DetailsPageFormFields.Gender],
      customOrNonBinaryGender:
        data[DetailsPageFormFields.GenderNonBinaryCustom],
      genderFurtherInformation: data[DetailsPageFormFields.GenderFurtherInfo],
      showGenderOnProfile: data[DetailsPageFormFields.DisplayGender],
      locationIds: data[DetailsPageFormFields.Location].map(
        (location) => location.id
      ),
      agencyRefNumber: data[DetailsPageFormFields.Agent],
    };
  };

  const clearHeadshotFormData = () => {
    if (headshotFormRef?.current) {
      const { setFieldValue } = headshotFormRef.current;
      setFieldValue("photo", []);
      setFieldValue("photographerName", "");
    }
  };

  const handleSubmitImageData = async (values: IAboutYouFormPhotoModel) => {
    try {
      setSubmittingHeadshot(true);
      setSubmittingForms(true);
      setShowUploadModal(false);

      const headshotResponse = await webMediaService.uploadHeadshot(
        mapHeadshotDataToBEModel(values),
        getAuthParams()
      );
      const responseText = await headshotResponse.text();

      if (!headshotResponse.ok) {
        throw new Error(responseText);
      }

      setShowUploadModal(false);
      setSubmittingHeadshot(false);
      setSubmittingForms(false);
    } catch (error) {
      clearHeadshotFormData();
      setSubmittingHeadshot(false);
      setSubmittingForms(false);
      setHeadshotUploadError(true);
    }
  };

  const handleSubmit = (values: IGraduatesDetailsFormFields) => {
    submitForm(formatData(values));
  };

  const handleLocationChange = (
    value: any,
    setFieldValue: (field: string, values: IGraduatesDetailsFormFields) => void
  ) => {
    setFieldValue(DetailsPageFormFields.Location, value.locations);
  };

  const onSearchChange = async (
    value: string
  ): Promise<ILocationContract[]> => {
    let locations: ILocationContract[] = [];
    const searchLocations = await locationService.searchLocation(value, "en");

    locations = searchLocations.map((item: Location) => ({
      description: item.description,
      placeId: item.place_id,
    }));

    return locations;
  };

  const roundLocation = async (placeId: string) => {
    const roundedLocations = await locationService.getRoundedLocation(
      placeId,
      "en"
    );

    return roundedLocations;
  };

  return (
    <PageTrackingLayoutWrapper loading={isLoading || locationsIsLoading}>
      <PageContent>
        {error && showErrorPopUp && (
          <CommonErrorPopup
            errorStatusCode={error?.response?.status}
            popupProps={{ close: () => setShowErrorPopUp(false) }}
          />
        )}
        <div className="mb-4 max-w-[780px]">
          <h1 className="my-4">
            {t("joining:graduate.details.page.header.title")}
          </h1>
          <p>{t("joining:graduate.details.page.header.description")}</p>
        </div>
        <Formik
          initialValues={getInitialValues(graduatesApplication, locationsData)}
          onSubmit={handleSubmit}
          validationSchema={getDetailsPageValidation(t)}
        >
          {({
            submitForm,
            setFieldValue,
            initialValues,
            values,
            errors,
            isSubmitting,
          }) => {
            return (
              <>
                <Form className="max-w-[780px] mb-10">
                  <AboutYouFormPart1
                    ref={(ref) => {
                      // @ts-expect-error -- AboutYouFormPart1 is rudimentary, I don't think we need to use is separate formik instance,
                      // must be just a field. Need to get access to photographerName not always possible to rerender (ref is not trigger re-render)
                      headshotFormRef.current = ref;
                      setHeadshotFormValid(!!ref?.values.photographerName);
                    }}
                    initialFormValues={{
                      photographerName: initialValues.photographerName,
                      photo: initialValues.photo,
                    }}
                    showUploadModal={showUploadModal}
                    setShowUploadModal={setShowUploadModal}
                    onSubmit={handleSubmitImageData}
                    headshotFormError={false}
                    submittingForm={submittingForms}
                    submittingHeadshot={submittingHeadshot}
                    clearHeadshotFormData={clearHeadshotFormData}
                    clearHeadshotError={() => {}}
                    headshotUploadError={headshotUploadError}
                  />
                  <hr />
                  {!fetchingAgencyOptions && agencyOptions.length > 0 && (
                    <div className="my-4">
                      <InputLabel
                        bold
                        label={t(
                          "joining:graduate.details.page.form.agent.title"
                        )}
                      />
                      <SelectFormik
                        name={DetailsPageFormFields.Agent}
                        label="If applicable, Please select your agent:"
                        options={agencyOptions}
                        annex={
                          <span className="text-sm text-color-neutral-two">
                            {`(${t("common:label.optional")})`}
                          </span>
                        }
                        searchableBySearchbox
                        isClearable
                      />
                      <p className="mb-4">
                        {t(
                          "joining:graduate.details.page.form.agent.description"
                        )}
                      </p>
                      <hr />
                    </div>
                  )}
                  <InputLabel
                    label={t("joining:graduate.details.page.form.gender.title")}
                    bold
                  />
                  <GenderSelection
                    names={{
                      main: DetailsPageFormFields.Gender,
                      customOrNonBinaryGender:
                        DetailsPageFormFields.GenderNonBinaryCustom,
                      furtherInformation:
                        DetailsPageFormFields.GenderFurtherInfo,
                      showGenderOnProfile: DetailsPageFormFields.DisplayGender,
                    }}
                    placeholder={t("joining:performer.form.gender.placeholder")}
                    pageTexts={{
                      findMeAsLabel: t(
                        "joining:performer.form.gender.findMeAsLabel"
                      ),
                      findMeAsInSearchResultsLabel: t(
                        "joining:performer.form.gender.findMeAsInSearchResultsLabel"
                      ),
                      furtherInformationLabel: t(
                        "joining:performer.form.gender.furtherInformationLabel"
                      ),
                      genderToBeDisplayedLabel: t(
                        "joining:performer.form.gender.genderToBeDisplayedLabel"
                      ),
                    }}
                    tooltipComponent={() => (
                      <QuestionTooltip
                        title={t(
                          "joining:performer.form.gender.tooltip.header"
                        )}
                        content={
                          <Trans
                            i18nKey="joining:performer.form.gender.tooltip.content"
                            components={{
                              a: (
                                <a
                                  href="https://www.spotlight.com/help-and-faqs/"
                                  target="_blank"
                                  rel="noreferrer"
                                  aria-label="FAQ"
                                />
                              ),
                            }}
                          />
                        }
                      />
                    )}
                  />
                  <hr className="mb-4" />
                  <InputLabel
                    label={t(
                      "joining:graduate.details.page.form.location.title"
                    )}
                    bold
                  />
                  <LocationList
                    model={{
                      locations: values[DetailsPageFormFields.Location],
                    }}
                    handleChanges={(value) =>
                      handleLocationChange(value, setFieldValue)
                    }
                    searchForLocation={onSearchChange}
                    roundLocation={roundLocation}
                    content={{
                      locationSectionName: "",
                      locationSectionDescription: "",
                      locationField: t(
                        "joining:performer.form.location.locationField"
                      ),
                      locationPlaceholder: t(
                        "joining:performer.form.location.locationPlaceholder"
                      ),
                    }}
                    name={DetailsPageFormFields.Location}
                    invalid={!!errors.location}
                    validationMessage={errors.location as string}
                  />
                </Form>
                <ActionsFooter
                  variant="secondary"
                  fullWidth
                  renderControl={() => (
                    <div className="flex flex-col-reverse justify-end gap-3 g-col-lg-12 md:flex-row">
                      <Button
                        type="secondary"
                        text={t("common:button.label.back")}
                        size="medium"
                        className="md:max-w-[280px]"
                        onClick={() =>
                          navigate(JOINING_APP_ROUTES.GRADUATE.ABOUT_YOU)
                        }
                      />
                      <Button
                        type="primary"
                        text={t("common:button.label.continue")}
                        size="medium"
                        className="md:max-w-[280px]"
                        onClick={submitForm}
                        loading={isSubmitting}
                        disabled={isSubmitting || !headshotFormValid}
                      />
                    </div>
                  )}
                />
              </>
            );
          }}
        </Formik>
      </PageContent>
    </PageTrackingLayoutWrapper>
  );
};

export default DetailsPageGrad;
